import React, { useEffect, useRef, useState, useMemo } from "react";
import Skeleton from "@material-ui/core/Skeleton";
import { sentenceCase } from "change-case";
import { useNavigate } from "react-router-dom";
// material
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import AttachmentIcon from "@material-ui/icons/Attachment";
import IconButton from "@material-ui/core/IconButton";
import * as locales from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Page from "../components/Page";
import Label from "../components/Label";
import Scrollbar from "../components/Scrollbar";
import { TareasListHead } from "../components/_dashboard/tareas";
import ModifyTaskStateModal from "../components/_dashboard/tareas/ModifyTaskStateModal";
import { Alerta } from "../components/Alerta";
import SimpleModal from "../layouts/SimpleModal";
import ArchivosAdjuntosDialog from "../components/tareas/ArchivosAdjuntosDialog";
import { formatDate } from "../utils/converter";
import SubTareasDialog from "src/components/tareas/SubTareasDialog";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  tareasApi,
  useGetClientesDistintosPorSucursalQuery,
  useGetEmpleadosDistintosPorSucursalQuery,
} from "../services/tareasApi";
import { useSnackbar } from "src/components/snackbar";
import useTareasFilters, {
  ESTADOS_TAREA,
} from "src/components/tareas/hooks/useTareasFilters";
import TareasFilterPanel from "src/components/tareas/filters/TareasFilterPanel";
import { aplicarFormatoFechas } from "../utils/formatTime";

const ESTADOS_PERMITIDOS_INICIO = [
  "PENDIENTE",
  "EN PROCESO",
  "OBSERVADA SII",
  "PENDIENTE DE FACTURACION",
];

// ----------------------------------------------------------------------

export default function InicioTabla(props) {
  const navigate = useNavigate();
  const { value } = props;
  const isMounted = useRef(true);
  const {
    empleado: user,
    sucursal: sucursalObtenida,
    rol: rolObtenido,
  } = useSelector((state) => state.auth.user);

  const {
    filtros,
    filtrosActivos,
    hayFiltrosActivos,
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,
    isAdmin,
  } = useTareasFilters([], { rolObtenido, storageKey: "tareasInicioFilters" });

  const [page, setPage] = useState(
    localStorage.getItem("pageHomeTask")
      ? parseInt(localStorage.getItem("pageHomeTask"), 10)
      : 0,
  );
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  // "fechaInicio" como orden por defecto: muestra primero las tareas más antiguas,
  // lo que ayuda a priorizar las que llevan más tiempo pendientes.
  const [orderBy, setOrderBy] = useState("fechaInicio");
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem("rowHomePerPageTask")
      ? parseInt(localStorage.getItem("rowHomePerPageTask"), 10)
      : 10,
  );
  const [locale] = useState("esES");
  const [showModifyTaskState, setShowModifyTaskState] = useState(false);
  const [selectedTask, setSelectedTask] = useState();
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [specificTask, setSpecificTask] = useState(null);
  const tableHead = useState(defineTableHeader())[0];
  const [mostrarSubTareas, setMostrarSubTareas] = useState(false);
  const [idSubTarea, setIdSubtarea] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const color = "success";
  const [showAlert, setShowAlert] = useState(false);

  const { data: tareasResponse, isLoading: loadingTable } =
    tareasApi.useGetTableroQuery(
      {
        refEmpleado: user.rut,
        sucursal: sucursalObtenida.refSucursal,
        page,
        limit: rowsPerPage,
        orderBy,
        order,
        busqueda: filtros.busqueda,
        estados:
          filtros.estados?.length > 0
            ? filtros.estados.join(",")
            : "PENDIENTE,EN PROCESO,OBSERVADA SII,PENDIENTE DE FACTURACION",
        tipoTarea: filtros.tipoTarea,
        ...aplicarFormatoFechas(filtros),
        cliente: filtros.cliente?.value || null,
        empleado: filtros.empleado?.value || null,
      },
      { skip: value !== 1 },
    );

  // ─── QUERY OPCIONES CLIENTES ─────────────────────────────────────────────────
  // Trae la lista de clientes únicos de la sucursal para el filtro dropdown.
  // rolObtenido y refEmpleado permiten al backend limitar clientes según permisos.
  const { data: clientesDistintos = [] } =
    useGetClientesDistintosPorSucursalQuery(
      {
        sucursal: sucursalObtenida.refSucursal,
        refEmpleado: user.rut,
        rolObtenido,
      },
      { skip: value !== 1 },
    );

  // ─── QUERY OPCIONES EMPLEADOS ────────────────────────────────────────────────
  // Solo se ejecuta si el usuario es admin/supervisor.
  // Los empleados no necesitan filtrar por "quién tiene asignada la tarea"
  // porque solo ven las suyas; los admins sí necesitan ese filtro.
  const { data: empleadosDistintos = [] } =
    useGetEmpleadosDistintosPorSucursalQuery(
      { sucursal: sucursalObtenida.refSucursal },
      { skip: !isAdmin || value !== 1 },
    );

  const [actualizarEstadoTarea] = tareasApi.useUpdateCambiaEstadoMutation();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  function defineTableHeader() {
    const tableHeadAux = [
      { id: "nombreCliente", label: "Nombre Cliente", alignRight: false },
      { id: "nombre", label: "Tarea", alignRight: false },
      { id: "tipoTarea", label: "Tipo Tarea", alignRight: false },
      { id: "fechaInicio", label: "Fecha Inicio", alignRight: false },
      {
        id: "diasTranscurridos",
        label: "Días Transcurridos",
        alignRight: false,
      },
      { id: "estado", label: "Estado", alignRight: false },
      { id: "subtareas", label: "", alignRight: false },
    ];
    return tableHeadAux;
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // tareasData: datos directos del servidor, ya no del estado local
      const newSelecteds = tareasData.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    localStorage.setItem("pageHomeTask", parseInt(newPage, 10));
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    localStorage.setItem(
      "rowHomePerPageTask",
      parseInt(event.target.value, 10),
    );
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTaskState = (id) => {
    // tareasData: buscamos la tarea en los datos del servidor, no en estado local
    const selectedTaskAux = tareasData.find((t) => t.id === id);
    setSelectedTask(selectedTaskAux);
    setShowModifyTaskState(true);
  };

  const doChangeTaskState = (newState) => {
    if (selectedTask.estado === newState) return setShowModifyTaskState(false);
    let modificacionEstado = {
      ...selectedTask,
      estado: newState,
    };

    if (newState === "TERMINADA") {
      if (selectedTask.tipoTarea === "ESPECIAL") {
        modificacionEstado.estado = "PENDIENTE DE FACTURACION";
      }
      modificacionEstado.fechaTermino = getToday();
    } else {
      modificacionEstado.fechaTermino = "";
    }
    setShowModifyTaskState(false);
    updateTaskInDB(modificacionEstado);
  };

  const getToday = () => {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getUTCFullYear();
    return formatDate(`${year}/${month}/${day}`);
  };

  const updateTaskInDB = (modificacionEstado) => {
    actualizarEstadoTarea({
      id: modificacionEstado.id,
      body: {
        estado: modificacionEstado.estado,
        fechaTermino: modificacionEstado.fechaTermino,
      },
    })
      .unwrap()
      .then(() => {
        enqueueSnackbar("Tarea actualizada correctamente.", {
          variant: "success",
        });
      })
      .catch(() => {
        enqueueSnackbar("No fue posible actualizar la tarea.", {
          variant: "error",
        });
        setSelectedTask(null);
      });
  };

  const handleAttachment = async (id) => {
    setSpecificTask(id);
    setShowAttachments(true);
  };

  const tareasData = useMemo(
    () => tareasResponse?.data ?? [],
    [tareasResponse],
  );

  // emptyRows: filas vacías para mantener altura constante de la tabla al paginar.
  // Se calcula sobre tareasData (lo que llegó del servidor para esta página).
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tareasData.length) : 0;

  // isElementNotFound: true cuando la página actual no tiene resultados.
  const isElementNotFound = tareasData.length === 0;

  const renderTableBody = () => {
    // Sin .slice(): el servidor ya devolvió solo la página solicitada
    return tareasData.map((row) => {
      const {
        id,
        nombre,
        tipoTarea,
        nombreCliente,
        fechaInicio,
        estado,
        diasTranscurridos,
      } = row;
      const isItemSelected = selected.indexOf(id) !== -1;
      let estiloFila = { backgroundColor: "#FFFFFF" };
      if (diasTranscurridos >= 14) {
        estiloFila = { backgroundColor: "#FFF9C4" };
      }

      return (
        <TableRow
          hover
          key={id}
          tabIndex={-1}
          role="checkbox"
          selected={isItemSelected}
          aria-checked={isItemSelected}
          style={estiloFila}
        >
          <TableCell
            style={{ cursor: "pointer" }}
            align="left"
            onClick={() => {
              navigate("/dashboard/tareas/" + id);
            }}
          >
            {nombreCliente}
          </TableCell>
          <TableCell
            style={{ cursor: "pointer" }}
            align="left"
            onClick={() => {
              navigate("/dashboard/tareas/" + id);
            }}
          >
            {nombre}
          </TableCell>
          <TableCell
            style={{ cursor: "pointer" }}
            align="left"
            onClick={() => {
              navigate("/dashboard/tareas/" + id);
            }}
          >
            {tipoTarea}
          </TableCell>
          <TableCell
            style={{ cursor: "pointer" }}
            align="left"
            onClick={() => {
              navigate("/dashboard/tareas/" + id);
            }}
          >
            {moment(fechaInicio, "YYYY/MM/DD").format("DD/MM/YYYY")}
          </TableCell>
          <TableCell
            style={{ cursor: "pointer" }}
            align="left"
            onClick={() => {
              navigate("/dashboard/tareas/" + id);
            }}
          >
            {diasTranscurridos === 1
              ? diasTranscurridos + " Día"
              : diasTranscurridos + " Días"}
          </TableCell>
          <TableCell align="left">
            <Label
              onClick={() => handleChangeTaskState(id)}
              style={{ cursor: "pointer" }}
              variant="ghost"
              color={
                (estado === "PENDIENTE" && "error") ||
                (estado === "EN PROCESO" && "warning") ||
                (estado === "TERMINADA" && "success") ||
                "info"
              }
            >
              {sentenceCase(estado)}
            </Label>
            {estado !== "TERMINADA" &&
              estado !== "PENDIENTE DE FACTURACION" &&
              estado !== "FACTURADO" && (
                <Tooltip title="Adjuntar">
                  <IconButton onClick={() => handleAttachment(row)}>
                    <AttachmentIcon />
                  </IconButton>
                </Tooltip>
              )}
          </TableCell>
          <TableCell align="left">
            <Label
              onClick={() => {
                setIdSubtarea(row);
                setMostrarSubTareas(true);
              }}
              style={{ cursor: "pointer" }}
              variant="ghost"
              color={"info"}
            >
              {sentenceCase("Subtareas")}
            </Label>
          </TableCell>
        </TableRow>
      );
    });
  };

  if (value !== 1) return <div />;

  return (
    <Page title="TAXTIC">
      {/* ── Panel de filtros avanzados ─────────────────────────────────────────
          Recibe las funciones y estado del hook useTareasFilters.
          opcionesClientes viene de getClientesDistintosPorSucursal y
          opcionesEmpleados de getEmpleadosDistintosPorSucursal; ambas alimentan
          los dropdowns de búsqueda por cliente/empleado.
          isAdmin controla si se muestra o no el filtro de empleados. */}
      <TareasFilterPanel
        filtros={filtros}
        updateFiltro={updateFiltro}
        toggleEstado={toggleEstado}
        limpiarFiltros={limpiarFiltros}
        removerFiltro={removerFiltro}
        filtrosActivos={filtrosActivos}
        hayFiltrosActivos={hayFiltrosActivos}
        opcionesClientes={clientesDistintos}
        opcionesEmpleados={empleadosDistintos}
        isAdmin={isAdmin}
        rutEmpleado={user.rut}
        // ─── Configurar qué filtros mostrar en InicioTabla ────────────
        showFilters={{
          busqueda: true,
          sucursales: false,
          estados: true,
          tipoTarea: true,
          cliente: false,
          empleado: false,
          fechas: false,
          soloEncargado: false,
        }}
        // ─── Solo mostrar 4 estados en los chips ──────────────────────
        estadosFiltrados={ESTADOS_TAREA.filter((estado) =>
          ESTADOS_PERMITIDOS_INICIO.includes(estado.value),
        )}
      />

      <Card style={{ marginTop: 5 }}>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            {!loadingTable ? (
              <Table>
                <TareasListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHead}
                  // total real del servidor, no el largo de la página actual
                  rowCount={
                    tareasResponse?.pagination?.total ?? tareasData.length
                  }
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {renderTableBody()}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                {isElementNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        No hay tareas que mostrar.
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            ) : (
              <Skeleton variant="rectangular" width="100%" height="500px" />
            )}
          </TableContainer>
        </Scrollbar>
        <ThemeProvider
          theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
        >
          {/* count usa el total real del servidor para que la paginación
              muestre las páginas correctas aunque solo tengamos 1 página en memoria */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={tareasResponse?.pagination?.total ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </ThemeProvider>
      </Card>

      {selectedTask && (
        <ModifyTaskStateModal
          task={selectedTask}
          onSubmit={doChangeTaskState}
          onClose={() => setShowModifyTaskState(false)}
          states={[
            "PENDIENTE",
            "EN PROCESO",
            "OBSERVADA SII",
            "TERMINADA",
            "FACTURADO",
          ]}
          open={showModifyTaskState}
          cargandoDatos={false}
        />
      )}

      <Alerta
        showAlert={showError}
        setShowAlert={setShowError}
        message={error}
        color="error"
      />

      {showAlert && (
        <Alerta
          showAlert={showAlert}
          setShowAlert={setShowAlert}
          message={error}
          color={color}
        />
      )}

      {specificTask && (
        <SimpleModal
          maxWidth="md"
          open={showAttachments}
          title="Archivo Adjunto"
          showCancelButton={false}
          showAcceptButton={false}
        >
          <ArchivosAdjuntosDialog
            tarea={specificTask}
            setTarea={setSpecificTask}
            setShowError={setShowError}
            setError={(msg) => {
              setError(msg);
              setShowError(true);
            }}
            onClose={() => setShowAttachments(false)}
          />
        </SimpleModal>
      )}
      {mostrarSubTareas && (
        <SubTareasDialog
          open={mostrarSubTareas}
          setOpen={setMostrarSubTareas}
          tarea={idSubTarea}
        />
      )}
    </Page>
  );
}
